import { ReactElement, useEffect, useState } from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { AgGrid, Flex, Tab, Tabs } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { ComplianceCaseStatus } from "~/graphql/supergraphEnums";
import {
  useGetActiveNonClosedCasesCountQuery,
  useGetPositionsWithoutCasesCountLazyQuery,
} from "~/graphql/supergraphHooks";
import { AssignProgramTable } from "~/pages/Compliance/AssignProgramTable";
import EntitiesExportButton from "~/pages/Compliance/AssignProgramTable/ExportButton";
import { ComplianceClientsTable } from "~/pages/Compliance/ComplianceClientsTable";
import { ClientsExportButton } from "~/pages/Compliance/ComplianceClientsTable/ExportButton";
import { ComplianceEntitiesTable } from "~/pages/Compliance/ComplianceEntitiesTable";
import { ComplianceTableContainer } from "~/pages/Compliance/ComplianceTable/ComplianceTableContainer";
import { User } from "~/types/user";
import { toGlobalId } from "~/utils/graphql-helpers";
import { getRedirectPath, isComplianceUser } from "~/utils/userRoles";
import CaseDetailsDrawer from "./CaseDetailsDrawer";
import PositionsTable from "./CompliancePositionsTable";
import CasesListFullScreen from "./CompliancePositionsTable/CasesListFullScreen";
import CaseButton from "./ComplianceTable/ComplianceTableContainer/CaseButton";
import { CreateCaseNavigation } from "./CreateCaseNavigation";
import { EntityClientCaseNavigation } from "./EntityClientCaseNavigation";
import { ComplianceEscalationsTableContainer } from "./EscalationsTable/ComplianceEscalationsTableContainer";
import { useComplianceEntityOnboardingEnabled, useComplianceProfileRefreshTab } from "./flags";
import { InvestorCaseNavigation } from "./InvestorCaseNavigation";
import { ProfilesTable } from "./ProfilesTable";
import { containerStyle, TabsContainer } from "./styles_vanilla.css";
import type { InvestmentEntitiesGraphqlDataSource } from "~/pages/Compliance/AssignProgramTable/investmentEntitiesGraphqlDataSource";

interface Props {
  currentUser: User;
}

export const Compliance = ({ currentUser }: Props): ReactElement => {
  const showProfilesTab = useComplianceProfileRefreshTab();
  const entityOnboardingEnabled = useComplianceEntityOnboardingEnabled();
  const showClientsTab = entityOnboardingEnabled;
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const arenaIds = queryParams.get("arenaIds") || undefined;
  const globalArenaId = arenaIds
    ? toGlobalId("ArenaObjectType", Number(arenaIds.split(",")[0]))
    : "";
  const entityId = queryParams.get("entityId") || undefined;
  const globalEntityId = entityId ? toGlobalId("AccountObjectType", Number(entityId)) : "";

  const [gridApiv2, setGridApiv2] = useState<AgGrid.GridApi>();
  const [showCreateNewCaseButton, setShowCreateNewCaseButton] = useState<boolean>(true);

  const [entitiesGridApi, setEntitiesGridApi] = useState<AgGrid.GridApi | null>(null);
  const [entitiesDataSource, setEntitiesDataSource] =
    useState<InvestmentEntitiesGraphqlDataSource | null>(null);

  const activeNonClosedCasesCount = useGetActiveNonClosedCasesCountQuery({
    variables: {
      filters: [
        {
          field: "status",
          filterValues: Object.values(ComplianceCaseStatus).filter(
            (status) =>
              status !== ComplianceCaseStatus.Closed &&
              status !== ComplianceCaseStatus.NotReady,
          ),
        },
      ],
    },
  });

  const [getPositionsQuery, positionsCountResult] = useGetPositionsWithoutCasesCountLazyQuery({
    variables: {
      positionFilters: {
        arenaIds: [globalArenaId],
        entityIds: [globalEntityId],
        includeFundAdminEntities: false,
      },
    },
  });

  const getPositionsWithoutCasesCount = () => {
    const totalCount = positionsCountResult.data?.positions?.count ?? 0;
    const completedPositionsCount =
      positionsCountResult.data?.positions?.edges.filter(
        (edge) => edge.node.status === "Completed",
      ).length || 0;
    let onboardedPositionCount = 0;
    positionsCountResult.data?.positions?.edges.forEach((edge) => {
      if (edge.node.status === "Active" && edge.node.onboardingCase)
        onboardedPositionCount += 1;
    });

    return totalCount - onboardedPositionCount - completedPositionsCount;
  };

  const getCompliancePath = (tab: string) => {
    return tab === "" ? globalEnv.complianceRoute : `${globalEnv.complianceRoute}/${tab}`;
  };
  const checkIsTabActive = (tab: string) => {
    return location.pathname === getCompliancePath(tab);
  };

  const isOnCasesTab = checkIsTabActive("");
  const isOnPositionsTab = checkIsTabActive("positions");

  useEffect(() => {
    // Reset when switching away from cases tab
    if (!isOnCasesTab) {
      setShowCreateNewCaseButton(true);
    }

    if (isOnPositionsTab && arenaIds && entityId) {
      void getPositionsQuery();
    }
  }, [
    arenaIds,
    entityId,
    getPositionsQuery,
    globalArenaId,
    globalEntityId,
    isOnCasesTab,
    isOnPositionsTab,
  ]);

  if (!isComplianceUser(currentUser.roles)) {
    return <Redirect to={getRedirectPath(currentUser)} />;
  }

  return (
    <div className={containerStyle}>
      <Flex
        className={TabsContainer}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Tabs hasPaddingInline={false}>
          <Tab
            data-testid="cases-tab"
            text="Cases"
            active={checkIsTabActive("")}
            counterBadgeProps={{
              count: activeNonClosedCasesCount.data?.complianceCasesSlice.filteredCount || 0,
              variant: "important",
            }}
            onClick={() => history.push(getCompliancePath(""))}
          />
          <Tab
            data-testid="escalations-tab"
            text="Escalations"
            active={checkIsTabActive("escalations")}
            onClick={() => history.push(getCompliancePath("escalations"))}
          />
          <Tab
            data-testid="positions-tab"
            text="Positions"
            active={checkIsTabActive("positions")}
            counterBadgeProps={{
              count:
                isOnPositionsTab && (!arenaIds || !entityId)
                  ? 0
                  : getPositionsWithoutCasesCount(),
              variant: "important",
            }}
            onClick={() =>
              history.push({
                pathname: getCompliancePath("positions"),
                search: location.search,
              })
            }
          />
          <Tab
            data-testid="entities-tab"
            text="Entities"
            active={checkIsTabActive("entities")}
            onClick={() => history.push(getCompliancePath("entities"))}
          />
          {showClientsTab && (
            <Tab
              data-testid="clients-tab"
              text="Clients"
              active={checkIsTabActive("clients")}
              onClick={() => history.push(getCompliancePath("clients"))}
            />
          )}
          {showProfilesTab && (
            <Tab
              data-testid="profiles-tab"
              text="Profiles"
              active={checkIsTabActive("profiles")}
              onClick={() => history.push(getCompliancePath("profiles"))}
            />
          )}
        </Tabs>
        {checkIsTabActive("") && (
          <CaseButton showCreateNewCaseButton={showCreateNewCaseButton} gridApi={gridApiv2} />
        )}
        {checkIsTabActive("entities") && !entityOnboardingEnabled && (
          <EntitiesExportButton gridApi={entitiesGridApi} dataSource={entitiesDataSource} />
        )}
        {showClientsTab && checkIsTabActive("clients") && <ClientsExportButton />}
      </Flex>
      <Switch>
        <Route path="/compliance/positions">
          <PositionsTable />
          <Route path="/compliance/positions/link-case">
            <CasesListFullScreen />
          </Route>
        </Route>
        <Route path="/compliance/escalations">
          <ComplianceEscalationsTableContainer />
          <Route path="/compliance/escalations/:caseId">
            <CaseDetailsDrawer />
          </Route>
        </Route>
        <Route path="/compliance/entities">
          {entityOnboardingEnabled ? (
            <ComplianceEntitiesTable />
          ) : (
            <AssignProgramTable
              setGridApi={setEntitiesGridApi}
              setDataSource={setEntitiesDataSource}
            />
          )}
          {entityOnboardingEnabled && (
            <Route path="/compliance/entities/link-case">
              <CasesListFullScreen subjectKind="entity" />
            </Route>
          )}
        </Route>
        {showClientsTab && (
          <Route path="/compliance/clients">
            <ComplianceClientsTable />
            <Route path="/compliance/clients/link-case">
              <CasesListFullScreen subjectKind="client" />
            </Route>
          </Route>
        )}
        {showProfilesTab && (
          <Route path="/compliance/profiles">
            <ProfilesTable />
          </Route>
        )}
        <Route path="/compliance">
          <ComplianceTableContainer
            setGridApiv2={setGridApiv2}
            setShowCreateNewCaseButtonv2={setShowCreateNewCaseButton}
          />
          <Switch>
            <Route path="/compliance/create-case/investor">
              <InvestorCaseNavigation />
            </Route>
            {entityOnboardingEnabled ? (
              <Route path="/compliance/create-case/entity">
                <EntityClientCaseNavigation caseType="entity" />
              </Route>
            ) : (
              <Redirect from="/compliance/create-case/entity" to={globalEnv.complianceRoute} />
            )}
            {entityOnboardingEnabled ? (
              <Route path="/compliance/create-case/client">
                <EntityClientCaseNavigation caseType="client" />
              </Route>
            ) : (
              <Redirect from="/compliance/create-case/client" to={globalEnv.complianceRoute} />
            )}
            <Route path="/compliance/case/:caseId">
              <CaseDetailsDrawer />
            </Route>
            <Route path="/compliance/create-case">
              <CreateCaseNavigation />
            </Route>
          </Switch>
        </Route>
      </Switch>
    </div>
  );
};
